import React, { cloneElement } from 'react';
import { motion } from 'framer-motion';
import { SparkleEffect } from '../components/SparkleEffect';
import { PlaneIcon, MapIcon, CompassIcon, SparklesIcon } from 'lucide-react';
import { GlitterHeart } from '../components/GlitterHeart';
export function TravelPage() {
  return <div className="min-h-screen bg-white pt-24 pb-24" data-id="element-526">
      {/* Hero */}
      <section className="relative py-20 text-center overflow-hidden bg-gradient-to-b from-sky-50 to-white" data-id="element-527">
        <SparkleEffect count={90} data-id="element-528" />
        <div className="max-w-3xl mx-auto px-4 relative z-10" data-id="element-529">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-heading text-5xl md:text-6xl font-bold text-plum-700 mb-6" data-id="element-530">
            Travel with <span className="shimmer-text" data-id="element-531">Mandy</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-plum-500 font-medium" data-id="element-532">
            Because everybody deserves to get the hell out sometimes.
          </motion.p>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-533">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="prose prose-lg prose-plum mx-auto text-plum-700 leading-relaxed text-center" data-id="element-534">
          <p className="text-2xl font-heading text-plum-700 mb-8 font-bold" data-id="element-535">
            I believe travel is healing.
          </p>
          <p className="mb-6 font-medium" data-id="element-536">
            Getting out of your everyday environment, seeing something new,
            breathing different air — it changes you. It resets your nervous
            system and reminds you that the world is bigger than whatever you're
            going through right now.
          </p>
          <p className="mb-6 font-medium" data-id="element-537">
            I don't care if your budget is $200 or $2,000. I will find you
            something. A weekend getaway, a road trip, a beach somewhere —
            everyone deserves to escape.
          </p>
          <p className="text-xl text-cta-500 font-bold mt-8" data-id="element-538">
            Tell me your budget, tell me your dream, and let me make it happen.
          </p>
        </motion.div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-blush-50" data-id="element-539">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-540">
          <h2 className="font-heading text-4xl font-bold text-plum-700 mb-16" data-id="element-541">
            How It Works
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-542">
            <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-0.5 bg-blush-200" data-id="element-543" />

            {[{
            icon: <MapIcon data-id="element-544" />,
            title: '1. Tell Me Your Budget',
            desc: 'No judgment, just honesty. We work with what you have.'
          }, {
            icon: <CompassIcon data-id="element-545" />,
            title: '2. I Find Your Perfect Trip',
            desc: 'From weekend getaways to week-long adventures, I handle the planning.'
          }, {
            icon: <PlaneIcon data-id="element-546" />,
            title: '3. You Go Live Your Best Life',
            desc: 'Pack your bags, escape, and come back recharged.'
          }].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="relative z-10" data-id="element-547">
                <div className="w-24 h-24 mx-auto bg-white rounded-full shadow-sm border border-blush-100 flex items-center justify-center text-cta-500 mb-6" data-id="element-548">
                  {cloneElement(step.icon as React.ReactElement, {
                className: 'w-10 h-10'
              })}
                </div>
                <h3 className="font-heading text-2xl font-bold text-plum-700 mb-3" data-id="element-549">
                  {step.title}
                </h3>
                <p className="text-plum-500 font-medium" data-id="element-550">{step.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Travel Types */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-551">
        <div className="text-center mb-16" data-id="element-552">
          <h2 className="font-heading text-4xl font-bold text-plum-700 mb-4" data-id="element-553">
            What We Can Plan
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-554">
          {[{
          title: 'Budget Getaways',
          desc: 'Weekend trips and local escapes under $500.',
          color: 'bg-blush-50 border-blush-100'
        }, {
          title: 'Healing Retreats',
          desc: 'Wellness-focused travel experiences to reset your soul.',
          color: 'bg-lavender-50 border-lavender-100'
        }, {
          title: 'Adventure Trips',
          desc: 'For when you need to feel alive and push your boundaries.',
          color: 'bg-sky-50 border-sky-100'
        }, {
          title: 'Group Travel',
          desc: 'Coming soon: group trips with the community. We rise together.',
          color: 'bg-white border-plum-100',
          badge: 'Coming Soon'
        }].map((type, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className={`p-8 rounded-3xl border ${type.color} shadow-sm relative`} data-id="element-555">
              {type.badge && <div className="absolute top-4 right-4 px-2 py-1 bg-plum-100 text-plum-700 text-xs font-bold rounded-md uppercase" data-id="element-556">
                  {type.badge}
                </div>}
              <SparklesIcon className="w-6 h-6 text-cta-500 mb-4" data-id="element-557" />
              <h3 className="font-heading text-xl font-bold text-plum-700 mb-2" data-id="element-558">
                {type.title}
              </h3>
              <p className="text-plum-500 font-medium text-sm" data-id="element-559">{type.desc}</p>
            </motion.div>)}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 text-center px-4" data-id="element-560">
        <div className="max-w-2xl mx-auto bg-gradient-to-br from-sky-100 to-white p-12 rounded-[3rem] shadow-xl border border-sky-200 relative overflow-hidden" data-id="element-561">
          <SparkleEffect count={50} data-id="element-562" />
          <div className="relative z-10" data-id="element-563">
            <h2 className="font-heading text-3xl font-bold text-plum-700 mb-4" data-id="element-564">
              Ready to pack your bags?
            </h2>
            <p className="text-plum-500 mb-8 font-medium" data-id="element-565">
              Currently booking for 2026. Reach out to start planning!
            </p>
            <button className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-cta-500 text-white font-bold text-lg hover:bg-cta-600 transition-all shadow-lg hover:-translate-y-1" data-id="element-566">
              <GlitterHeart size={20} variant="light" data-id="element-567" />
              Let's Plan Your Escape
            </button>
          </div>
        </div>
      </section>
    </div>;
}